import { addActionHandler } from '../global';

import type { ApiMessage } from '../api/types';
import type { ActionReturnType } from '../global/types';
import type { ForwardTask } from './seatBridge';
import { ApiMessageEntityTypes } from '../api/types';

import { pause } from '../util/schedulers';
import { getServerTime } from '../util/serverTime';
import { callApi } from '../api/gramjs';
import {
  getIsSeatAccountVip, getIsSeatForwardAllowed, getSeatRelayConfig, reportSeatCooldown, reportSeatForwardResult,
  reportSeatMessage, requestForwardText,
} from './seatBridge';
import {
  COOLDOWN_WHEN_SPAM_BOT_SEES_NO_LIMIT, COOLDOWN_WHEN_SPAM_BOT_UNKNOWN, fetchSeatCooldownSeconds,
} from './seatStatus';

// Relay tasks arrive as a private message from the platform bot in the same format the server-side
// forwarder already uses: the text is `RELAY:<targetUserId>`, optionally followed by ` @username`
// and ` #<serverTaskId>`. A target with a username is resolved by it, otherwise the target comes
// from the attached `messageEntityMentionName`, which resolves a user the seat account has no
// access hash for. The server task id is reported back so the result matches its own task exactly
const RELAY_PREFIX = 'RELAY:';
const SERVER_TASK_PREFIX = '#';

// A Premium account is only cooled down on every second flood error; the odd ones just ping SpamBot,
// which is what unlocks such accounts in practice
const VIP_FLOOD_COOLDOWN = 3600;

// 同一个目标 60 秒内不重复拉会话
const REVEAL_INTERVAL = 60;

// 文案发完隔 1 秒再发名片，不让两条消息同一瞬间出去
const CARD_DELAY_MS = 1000;

let vipFloodCount = 0;
const revealedChats = new Map<string, number>();

// Only tasks that arrive while the seat is online are handled: the client starts from the current
// update state, so a task pushed while the page was closed is left to the server-side timeout
addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  if (update['@type'] !== 'updateMessage' || !update.isFromNew) return;

  handleRelayMessage(update.message);
});

function handleRelayMessage(message: Partial<ApiMessage>) {
  // 服务器托管已接管时不接活：中转消息留给服务端常驻进程处理（包括删除），
  // 两端同时发会把同一个目标私聊两次
  if (!getIsSeatForwardAllowed()) return;

  const { relayBotId, maxRelayAge } = getSeatRelayConfig();
  if (!relayBotId || message.chatId !== relayBotId || message.isOutgoing) return;
  if (!message.id || !message.date) return;

  const textContent = message.content?.text;
  const text = textContent?.text;
  if (!text?.startsWith(RELAY_PREFIX)) return;

  const [taskId, ...parts] = text.slice(RELAY_PREFIX.length).trim().split(/\s+/);
  const usernamePart = parts.find((part) => part.startsWith('@'));
  const targetUsername = usernamePart ? usernamePart.slice(1) : undefined;
  const serverTaskPart = parts.find((part) => part.startsWith(SERVER_TASK_PREFIX));
  const serverTaskId = serverTaskPart ? serverTaskPart.slice(SERVER_TASK_PREFIX.length) : undefined;

  // The task age is measured against Telegram time, so a skewed local clock does not drop live tasks
  if (maxRelayAge && getServerTime() - message.date > maxRelayAge) {
    reportSeatMessage(`私聊任务已过期，忽略：${taskId}`, 'error', 'forward');
    void callApi('deleteSeatRelayMessage', { messageId: message.id });
    return;
  }

  const mention = textContent?.entities?.find((entity) => (
    entity.type === ApiMessageEntityTypes.MentionName
  ));
  const mentionedUserId = mention?.type === ApiMessageEntityTypes.MentionName ? mention.userId : undefined;
  const targetUserId = mentionedUserId ?? taskId;

  if (!targetUsername && !mentionedUserId) {
    reportSeatMessage(
      `私聊任务缺少目标用户提及，已忽略：目标 ${taskId}，任务 ${serverTaskId || '-'}，中转消息 ${message.id}`,
      'error',
      'forward',
    );
    void callApi('deleteSeatRelayMessage', { messageId: message.id });
    return;
  }

  void deliverRelayMessage({
    taskId,
    serverTaskId,
    sourceChatId: relayBotId,
    sourceMessageId: message.id,
    targetUserId,
    targetUsername,
  });
}

async function deliverRelayMessage({
  taskId, serverTaskId, sourceChatId, sourceMessageId, targetUserId, targetUsername,
}: {
  taskId: string;
  serverTaskId?: string;
  sourceChatId: string;
  sourceMessageId: number;
  targetUserId: string;
  targetUsername?: string;
}) {
  let task: ForwardTask;
  try {
    task = await requestForwardText(serverTaskId ?? taskId);
  } catch (err) {
    const reason = getErrorText(err);
    reportSeatMessage(`私聊失败 目标 ${targetUserId}：${reason}`, 'error', 'forward');
    reportSeatForwardResult(serverTaskId, false, 'forward_text_missing', reason);
    void callApi('deleteSeatRelayMessage', { messageId: sourceMessageId });
    return;
  }

  try {
    const result = await deliverTask(task, {
      sourceChatId, sourceMessageId, targetUserId, targetUsername,
    });

    if (result) {
      reportSeatMessage(`私聊成功 目标 ${targetUserId}`, 'info', 'forward');
      // 文案和核对清单可附加名片；转发任务不发，card 任务已经在 deliverTask 里发过
      if (task.mode === 'text' || task.mode === 'todo') {
        await deliverCard(task, {
          sourceChatId, sourceMessageId, targetUserId, targetUsername,
        });
      }
      reportSeatForwardResult(serverTaskId, true);
      await revealTargetChat({
        sourceChatId, sourceMessageId, targetUserId, targetUsername,
      });
    } else {
      const reason = task.mode === 'forward' ? '要转发的消息或来源频道打不开' : '发送未返回结果';
      reportSeatMessage(`私聊失败 目标 ${targetUserId}：${reason}`, 'error', 'forward');
      reportSeatForwardResult(
        serverTaskId,
        false,
        task.mode === 'forward' ? 'forward_source_missing' : 'unknown',
        reason,
      );
    }
  } catch (err) {
    const reason = getErrorText(err);
    reportSeatMessage(`私聊失败 目标 ${targetUserId}：${reason}`, 'error', 'forward');

    // 限流的处理结果决定回传的错误类型：被判定冻结/需申诉时服务端要把这个号的状态写下来，
    // 所以先问完 SpamBot 再回结果
    let errorType = getForwardErrorType(reason);
    if (isFloodError(reason)) {
      const outcome = await applyFloodCooldown(reason);
      if (outcome === 'frozen') errorType = 'account_frozen';
      if (outcome === 'appeal_required') errorType = 'account_appeal_required';
    }

    reportSeatForwardResult(serverTaskId, false, errorType, reason);
  }

  // The mention entity of the relay message is needed to resolve a target without a username,
  // so the message is only removed once the task is done
  void callApi('deleteSeatRelayMessage', { messageId: sourceMessageId });
}

// 文案任务发文字，转发任务把公开频道的那条消息原样转给目标，只配了名片没配文案时名片就是这单本身；
// 三种都支持「有用户名」和「只有提及实体」两类目标，后者的 peer 从中转消息里取
function deliverTask(task: ForwardTask, target: {
  sourceChatId: string;
  sourceMessageId: number;
  targetUserId: string;
  targetUsername?: string;
}) {
  const {
    sourceChatId, sourceMessageId, targetUserId, targetUsername,
  } = target;

  if (task.mode === 'card') {
    return callApi('sendSeatContactCard', {
      sourceChatId,
      sourceMessageId,
      targetUserId,
      targetUsername,
      phone: task.card!.phone,
      firstName: task.card!.firstName,
    });
  }

  if (task.mode === 'forward') {
    return callApi('forwardSeatMessage', {
      fromUsername: task.sourceUsername!,
      fromMessageId: task.sourceMessageId!,
      sourceChatId,
      sourceMessageId,
      targetUserId,
      targetUsername,
    });
  }

  if (task.mode === 'todo') {
    if (!getIsSeatAccountVip()) {
      return Promise.reject(new Error('TODO_PREMIUM_REQUIRED'));
    }

    return callApi('sendSeatTodo', {
      sourceChatId,
      sourceMessageId,
      targetUserId,
      targetUsername,
      todo: task.todo!,
    });
  }

  const text = task.text || '';

  return targetUsername
    ? callApi('sendSeatUsernameMessage', { username: targetUsername, text })
    : callApi('sendSeatMentionMessage', { sourceChatId, sourceMessageId, targetUserId, text });
}

// 推送名片：配了就在文案或核对清单发成功之后再发一条联系人名片。
// 名片发不出去不能把已经发成功的内容当成私聊失败（否则服务端会换个席位号重发，目标收到两次），
// 所以只写一条日志
async function deliverCard(task: ForwardTask, target: {
  sourceChatId: string;
  sourceMessageId: number;
  targetUserId: string;
  targetUsername?: string;
}) {
  const { card } = task;
  if (!card?.phone) return;

  const {
    sourceChatId, sourceMessageId, targetUserId, targetUsername,
  } = target;

  await pause(CARD_DELAY_MS);

  try {
    const sent = await callApi('sendSeatContactCard', {
      sourceChatId,
      sourceMessageId,
      targetUserId,
      targetUsername,
      phone: card.phone,
      firstName: card.firstName,
    });

    reportSeatMessage(
      sent
        ? `名片已推送 目标 ${targetUserId}：${card.phone}`
        : `名片推送失败 目标 ${targetUserId}：发送未返回结果`,
      sent ? 'info' : 'error',
      'forward',
    );
  } catch (err) {
    reportSeatMessage(`名片推送失败 目标 ${targetUserId}：${getErrorText(err)}`, 'error', 'forward');
  }
}

/**
 * 私聊发出去之后把这个会话拉进界面：消息是直接调 MTProto 发的（限流、冻结这些错误要同步拿到才
 * 能写冷却和回传派单结果），不经过本地会话，所以发完再拉一次，不刷新也能看到刚发的内容。
 * 同一个目标在短时间内只拉一次
 */
async function revealTargetChat({
  sourceChatId, sourceMessageId, targetUserId, targetUsername,
}: {
  sourceChatId: string;
  sourceMessageId: number;
  targetUserId: string;
  targetUsername?: string;
}) {
  const now = getServerTime();
  const revealedAt = revealedChats.get(targetUserId);
  if (revealedAt && now - revealedAt < REVEAL_INTERVAL) return;

  try {
    const chat = targetUsername
      ? (await callApi('getChatByUsername', targetUsername))?.chat
      : await callApi('fetchSeatMentionChat', { sourceChatId, sourceMessageId, targetUserId });
    if (!chat) return;

    revealedChats.set(targetUserId, now);
    await callApi('requestChatUpdate', { chat });
  } catch (err) {
    // 拉不出来只是界面上看不到，消息已经发成功，不能把它当成私聊失败
    reportSeatMessage(`会话拉取失败 目标 ${targetUserId}：${getErrorText(err)}`, 'info', 'forward');
  }
}

// Only the errors of resolving and messaging the target are reported: the ones about building the
// mention (privacy, deleted source message, missing user info) are already decided on the server,
// which resolves them without waiting for the browser
function getForwardErrorType(reason: string) {
  const upper = reason.toUpperCase();

  if (isFloodError(upper)) return 'flood_wait';
  if (isSessionRevokedError(upper)) return 'session_revoked';
  if (upper.includes('PREMIUM')
    && upper.includes('REQUIRED')
    && !upper.includes('PRIVACY_PREMIUM_REQUIRED')) return 'todo_premium_required';
  // 对方只收联系人/会员的私聊，或要求先付 Star：换哪个号都发不出去
  if (upper.includes('USER_PRIVACY_RESTRICTED')
    || upper.includes('PRIVACY_PREMIUM_REQUIRED')
    || upper.includes('ALLOW_PAYMENT_REQUIRED')
    || upper.includes('ALLOW_PAID_STARS')
    || (upper.includes('PEER CHARGES') && upper.includes('TELEGRAM STARS'))
    || upper.includes('BALANCE_TOO_LOW')) return 'privacy_restricted';
  if (upper.includes('USER_IS_BLOCKED') || upper.includes('YOU_BLOCKED_USER')) return 'user_blocked';
  if (upper.includes('USER_BANNED')) return 'user_info_failed';
  if (upper.includes('USERNAME_NOT_OCCUPIED') || upper.includes('USERNAME_INVALID')) return 'username_not_found';
  // 转发私聊：要转发的那条消息没了 / 来源频道打开不了或禁止转发，换席位号也没用
  if (upper.includes('MESSAGE_ID_INVALID')
    || upper.includes('MESSAGE_IDS_EMPTY')
    || upper.includes('CHANNEL_INVALID')
    || upper.includes('CHANNEL_PRIVATE')) return 'forward_source_missing';
  if (upper.includes('CHAT_FORWARDS_RESTRICTED')
    || upper.includes('MESSAGE_FORWARDS_RESTRICTED')) return 'forward_source_restricted';
  if (upper.includes('CHAT_WRITE_FORBIDDEN') || upper.includes('CHAT_SEND_PLAIN_FORBIDDEN')) return 'forward_failed';

  return 'unknown';
}

// Mirrors the server-side flood handling: a Premium account pings SpamBot on odd errors and is only
// cooled down on even ones, a regular account asks SpamBot for the real remaining time
async function applyFloodCooldown(reason: string): Promise<'frozen' | 'appeal_required' | 'cooldown'> {
  if (getIsSeatAccountVip()) {
    vipFloodCount += 1;
    if (vipFloodCount % 2 === 1) {
      // 发 /start 后的回复同样要看一眼：判定冻结/需申诉就把状态写下来（与公共代发一致）
      const status = await fetchSeatCooldownSeconds().catch(() => undefined);
      if (status === 'frozen' || status === 'appeal_required') {
        return reportAbnormalStatus(status);
      }

      reportSeatMessage('会员账号被限流，已向 SpamBot 发送 /start，不标记冷却', 'info', 'forward');
      return 'cooldown';
    }

    reportSeatCooldown(VIP_FLOOD_COOLDOWN, 'vip_flood_cooldown');
    return 'cooldown';
  }

  const waitSeconds = getFloodWaitSeconds(reason) ?? await resolveCooldownFromSpamBot();
  if (waitSeconds === 'frozen' || waitSeconds === 'appeal_required') {
    return reportAbnormalStatus(waitSeconds);
  }

  reportSeatCooldown(waitSeconds, isSpamReportedError(reason) ? 'spam_reported' : 'flood_wait');

  return 'cooldown';
}

// 与公共代发同口径：这两种号会被状态码踢出选号范围，再记冷却没有意义
function reportAbnormalStatus(status: 'frozen' | 'appeal_required') {
  reportSeatMessage(
    status === 'frozen' ? '账号已被冻结' : '账号被限制，需要申诉解除',
    'error',
    'forward',
  );

  return status;
}

async function resolveCooldownFromSpamBot() {
  let seconds: number | 'frozen' | 'appeal_required' | undefined;
  try {
    seconds = await fetchSeatCooldownSeconds();
  } catch (err) {
    return COOLDOWN_WHEN_SPAM_BOT_UNKNOWN;
  }

  if (seconds === 'frozen' || seconds === 'appeal_required') return seconds;
  if (seconds === undefined) return COOLDOWN_WHEN_SPAM_BOT_UNKNOWN;

  return seconds > 0 ? seconds : COOLDOWN_WHEN_SPAM_BOT_SEES_NO_LIMIT;
}

// 席位号自己的登录已经废了（被踢下线、session 被撤销、号被注销或封禁），目标本身没问题，交给下一个席位号发
export function isSessionRevokedError(upper: string) {
  return upper.includes('AUTH_KEY_UNREGISTERED')
    || upper.includes('AUTH_KEY_DUPLICATED')
    || upper.includes('SESSION_REVOKED')
    || upper.includes('SESSION_EXPIRED')
    || upper.includes('USER_DEACTIVATED');
}

function isFloodError(reason: string) {
  const upper = reason.toUpperCase();
  return upper.includes('FLOOD')
    || upper.includes('TOO MANY REQUESTS')
    || isSpamReportedError(upper);
}

function isSpamReportedError(reason: string) {
  const upper = reason.toUpperCase();

  return upper.includes('SPAMREPORTED')
    || upper.includes('SPAM_REPORTED')
    || upper.includes('SPAM REPORTED')
    || upper.includes('CHECK @SPAMBOT');
}

// `FLOOD_WAIT_120` already carries the exact wait, so SpamBot is only asked when it does not
function getFloodWaitSeconds(reason: string) {
  const match = reason.match(/FLOOD_WAIT_(\d+)/i);
  return match ? Number(match[1]) : undefined;
}

// Errors crossing the worker boundary arrive as plain objects, so `message` is read from them too
function getErrorText(err: unknown) {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && 'message' in err) return String(err.message);

  return String(err);
}
